import { useMemo, useCallback, FC } from "react";
import { Controller, useFormContext, useFieldArray } from "react-hook-form";
import {
  Autocomplete,
  TextField,
  Chip,
  CircularProgress,
  Button,
  Box,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import __ from "src/utils/Translations";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { absencesAbsencesConfigurationWrite } from "src/utils/RoleBasedSecurity/permissions";
import {
  useFetchEmployeeGroups,
  useFetchOrganizationUnits,
} from "src/Pages/Company/Absences/api/useEmployee";
import { getEmploymentFormOptions } from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/EmployeeEmployment/constants/employmentForms";

interface IEntitledEmployeesProps {
  disabled: boolean;
}
const EntitledEmployees: FC<IEntitledEmployeesProps> = ({ disabled }) => {
  const { control } = useFormContext();
  const companyId = useCompanyId();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "entitledEmployees.costPlaces",
  });

  const employmentForms = useMemo(() => getEmploymentFormOptions(), []);

  const { data: employeeGroups = [], isLoading: isLoadingEmployeeGroups } =
    useFetchEmployeeGroups(companyId);

  const {
    data: organizationUnits = [],
    isLoading: isLoadingOrganizationUnits,
  } = useFetchOrganizationUnits(companyId);

  const employeeGroupsOptions = useMemo(
    () =>
      employeeGroups.map((group) => ({
        id: group.id,
        label: group.name,
      })),
    [employeeGroups]
  );

  const organizationUnitsOptions = useMemo(
    () =>
      organizationUnits.map((unit) => ({
        id: unit.id,
        label: unit.name,
      })),
    [organizationUnits]
  );

  const filterOptions = useCallback(
    (options: { id: string }[], value: string[]) =>
      value ? options.filter((o) => value.includes(o.id)) : [],
    []
  );

  const filterEmploymentOptions = useCallback(
    (value: string[]) =>
      value ? employmentForms.filter((o) => value.includes(o.value)) : [],
    [employmentForms]
  );

  return (
    <>
      <Controller
        name="entitledEmployees.employmentTypes"
        control={control}
        render={({
          field: { value, onChange, ref },
          fieldState: { invalid, error },
        }) => (
          <Autocomplete
            multiple
            options={employmentForms}
            disabled={disabled}
            value={filterEmploymentOptions(value)}
            onChange={(_event, newValue) =>
              onChange(newValue.map((option) => option.value))
            }
            getOptionLabel={(option) => option.label}
            filterSelectedOptions
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option.label} {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={__("Forma zatrudnienia")}
                margin="normal"
                error={invalid}
                helperText={error?.message}
                inputRef={ref}
                data-testid="absence-type-form-field__entitled-employees-employment-types"
              />
            )}
          />
        )}
      />

      <Controller
        name="entitledEmployees.employeeGroups"
        control={control}
        render={({
          field: { value, onChange, ref },
          fieldState: { invalid, error },
        }) => (
          <Autocomplete
            multiple
            options={employeeGroupsOptions}
            value={filterOptions(employeeGroupsOptions, value)}
            onChange={(_event, newValue) =>
              onChange(newValue?.map((v) => v.id))
            }
            loading={isLoadingEmployeeGroups}
            disabled={disabled}
            filterSelectedOptions
            renderInput={(params) => (
              <TextField
                {...params}
                label={__("Grupa pracownicza")}
                margin="normal"
                error={invalid}
                helperText={error?.message}
                inputRef={ref}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingEmployeeGroups ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
                data-testid="absence-type-form-field__entitled-employees-employee-groups"
              />
            )}
          />
        )}
      />

      <Controller
        name="entitledEmployees.organizationUnits"
        control={control}
        render={({
          field: { value, onChange, ref },
          fieldState: { invalid, error },
        }) => (
          <Autocomplete
            multiple
            options={organizationUnitsOptions}
            value={filterOptions(organizationUnitsOptions, value)}
            onChange={(_event, newValue) =>
              onChange(newValue?.map((v) => v.id))
            }
            loading={isLoadingOrganizationUnits}
            disabled={disabled}
            filterSelectedOptions
            renderInput={(params) => (
              <TextField
                {...params}
                label={__("Jednostka organizacyjna")}
                margin="normal"
                error={invalid}
                helperText={error?.message}
                inputRef={ref}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingOrganizationUnits ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
                data-testid="absence-type-form-field__entitled-employees-organization-units"
              />
            )}
          />
        )}
      />

      <Box sx={{ mt: 2 }}>
        {fields.map((field, index) => (
          <Box
            key={field.id}
            sx={{ display: "flex", alignItems: "center", mb: 1 }}
          >
            <Controller
              name={`entitledEmployees.costPlaces.${index}`}
              control={control}
              render={({
                field: { value, onChange, ref },
                fieldState: { invalid, error },
              }) => (
                <TextField
                  fullWidth
                  value={value}
                  disabled={disabled}
                  onChange={onChange}
                  inputRef={ref}
                  label={__("MPK")}
                  error={invalid}
                  helperText={error?.message}
                  sx={{ mb: 1, mr: 1 }}
                  data-testid="absence-type-form-field__entitled-employees-cost-places"
                />
              )}
            />
            {!disabled && (
              <IconButton
                onClick={() => remove(index)}
                color="error"
                size="small"
                sx={{ mt: -1 }}
                data-testid="absence-type-form-field__entitled-employees-mpk-remove-action"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        ))}

        {!disabled && (
          <Button
            startIcon={<AddIcon />}
            onClick={() => append("")}
            variant="text"
            size="small"
            sx={{ mt: 1 }}
            data-testid="absence-type-form-field__entitled-employees-mpk-add-action"
          >
            {__("Dodaj MPK")}
          </Button>
        )}
      </Box>
    </>
  );
};

export default EntitledEmployees;
